import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch } from '../../../../lib/api';
import { formatCurrency } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';

interface PreventivoRiga {
  id: number;
  numero: string;
  tipo: 'SEMPLICE' | 'CONFIGURATO_COMMESSA';
  stato: string;
  versione: number;
  oggetto: string;
  dataEmissione: string;
  dataValidita: string | null;
  totaleCliente: string | null;
  clienteRagioneSociale: string | null;
  clienteNome: string | null;
  clienteCognome: string | null;
  acquirente: {
    id: number;
    nome: string | null;
    cognome: string | null;
    unitaCodice: string;
  } | null;
}

interface AcquirenteOption {
  id: number;
  label: string;
}

interface Props {
  commessaId: number;
  societaId: number;
  filtroAcquirente?: { id: number; label: string } | null;
  onResetFiltroAcquirente?: () => void;
  onNuovoPreventivo: () => void;
}

const STATO_LABEL: Record<string, string> = {
  BOZZA: 'Bozza',
  INVIATO: 'Inviato',
  ACCETTATO: 'Accettato',
  RIFIUTATO: 'Rifiutato',
  SCADUTO: 'Scaduto',
  CONVERTITO: 'Convertito',
  ANNULLATO: 'Annullato',
  ANNULLATO_DOPO_CONFERMA: 'Annullato dopo conferma',
  SUPERATO: 'Superato',
};

const ChevronIcon = () => (
  <svg className="bop-tab-preventivi__acq-chevron" width="12" height="12" viewBox="0 0 24 24"
       fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default function TabPreventivi({
  commessaId,
  societaId,
  filtroAcquirente,
  onResetFiltroAcquirente,
  onNuovoPreventivo,
}: Props) {
  const { can } = usePermesso();
  const canEdit = can('commerciale.preventivi', 'edit');

  const [preventivi, setPreventivi] = useState<PreventivoRiga[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Acquirenti per la combo
  const [acquirenteOpts, setAcquirenteOpts] = useState<AcquirenteOption[]>([]);
  const [filtroLocale, setFiltroLocale] = useState<AcquirenteOption | null>(null);
  const [comboOpen, setComboOpen] = useState(false);
  const [comboSearch, setComboSearch] = useState('');
  const comboRef = useRef<HTMLDivElement>(null);

  // Il filtro esterno (da tab Acquirenti) ha precedenza sul locale
  const effectiveFilter = filtroAcquirente ?? filtroLocale;

  // Carica lista acquirenti per popolare la combo
  useEffect(() => {
    // @api GET /api/commesse/:id/acquirenti -> {"success":true,"data":[{"id":"number","nome":"string","cognome":"string","unitaCodice":"string"}]}
    apiFetch<{ success: boolean; data: { id: number; nome: string | null; cognome: string | null; unitaCodice: string }[] }>(
      `/api/commesse/${commessaId}/acquirenti`
    )
      .then(res => setAcquirenteOpts(
        res.data.map(a => ({
          id: a.id,
          label: `${a.unitaCodice} — ${[a.nome, a.cognome].filter(Boolean).join(' ')}`,
        }))
      ))
      .catch(() => {});
  }, [commessaId]);

  // Chiudi combo con click esterno
  useEffect(() => {
    if (!comboOpen) return;
    const handler = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setComboOpen(false);
        setComboSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [comboOpen]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const acqQ = effectiveFilter?.id ? `&acquirenteId=${effectiveFilter.id}` : '';
      // @api GET /api/preventivi?societaId={id}&commessaId={commessaId}&acquirenteId={acqId} -> {"success":"true","data":[{"id":"number","numero":"string","tipo":"string","stato":"string","versione":"number","oggetto":"string","dataEmissione":"string","dataValidita":"string","totaleCliente":"string","clienteRagioneSociale":"string","clienteNome":"string","clienteCognome":"string","acquirente":{"id":"number","nome":"string","cognome":"string","unitaCodice":"string"}}]}
      const res = await apiFetch<{ success: boolean; data: PreventivoRiga[] }>(
        `/api/preventivi?societaId=${societaId}&commessaId=${commessaId}${acqQ}`
      );
      setPreventivi(res.data || []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [commessaId, societaId, effectiveFilter?.id]);

  useEffect(() => { load(); }, [load]);

  const selectAcquirente = (opt: AcquirenteOption | null) => {
    // Rimuovi il filtro esterno se presente
    if (filtroAcquirente) onResetFiltroAcquirente?.();
    setFiltroLocale(opt);
    setComboOpen(false);
    setComboSearch('');
  };

  const clearFilter = () => {
    if (filtroAcquirente) onResetFiltroAcquirente?.();
    setFiltroLocale(null);
  };

  const opzioniFiltrate = acquirenteOpts.filter(o =>
    comboSearch === '' || o.label.toLowerCase().includes(comboSearch.toLowerCase())
  );

  const apriPreventivo = (id: number) => {
    window.open(`/commerciale/attivo/preventivi/${id}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="bop-text-muted bop-tab-preventivi__loading">Caricamento preventivi…</div>;
  }
  if (error) {
    return <div className="bop-text-danger bop-tab-preventivi__error">{error}</div>;
  }

  return (
    <div className="bop-tab-preventivi">
      <div className="bop-page-header bop-tab-preventivi__header">
        <h2 className="bop-page-title bop-tab-preventivi__title">Preventivi della commessa</h2>
        {canEdit && (
          <button className="bop-btn bop-btn-primary" onClick={onNuovoPreventivo}>
            + Nuovo preventivo
          </button>
        )}
      </div>

      <div className="bop-tab-preventivi__filter-bar">
        <div className="bop-tab-preventivi__acq-combo" ref={comboRef}>
          <span className="bop-tab-preventivi__acq-label">Acquirente</span>
          <button
            className={`bop-tab-preventivi__acq-btn${effectiveFilter ? ' bop-tab-preventivi__acq-btn--active' : ''}`}
            onClick={() => { setComboOpen(o => !o); setComboSearch(''); }}
            type="button"
          >
            <span className="bop-tab-preventivi__acq-btn-text">
              {effectiveFilter?.label ?? 'Tutti gli acquirenti'}
            </span>
            {effectiveFilter ? (
              <span
                className="bop-tab-preventivi__acq-clear"
                onClick={e => { e.stopPropagation(); clearFilter(); }}
                title="Rimuovi filtro"
              >
                ×
              </span>
            ) : (
              <ChevronIcon />
            )}
          </button>

          {comboOpen && (
            <div className="bop-tab-preventivi__acq-dropdown">
              <input
                className="bop-tab-preventivi__acq-search"
                placeholder="Cerca acquirente…"
                value={comboSearch}
                onChange={e => setComboSearch(e.target.value)}
                autoFocus
              />
              <ul className="bop-tab-preventivi__acq-list">
                <li
                  className="bop-tab-preventivi__acq-option bop-tab-preventivi__acq-option--all"
                  onClick={() => selectAcquirente(null)}
                >
                  Tutti gli acquirenti
                </li>
                {opzioniFiltrate.length === 0 ? (
                  <li className="bop-tab-preventivi__acq-empty">Nessun risultato</li>
                ) : (
                  opzioniFiltrate.map(o => (
                    <li
                      key={o.id}
                      className={`bop-tab-preventivi__acq-option${effectiveFilter?.id === o.id ? ' bop-tab-preventivi__acq-option--selected' : ''}`}
                      onClick={() => selectAcquirente(o)}
                    >
                      {o.label}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {preventivi.length === 0 ? (
        <div className="bop-empty-state bop-tab-preventivi__empty">
          {effectiveFilter
            ? `Nessun preventivo per ${effectiveFilter.label}.`
            : 'Nessun preventivo collegato a questa commessa.'}
        </div>
      ) : (
        <div className="bop-card bop-tab-preventivi__card">
          <table className="bop-table bop-tab-preventivi__table">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Tipo</th>
                <th>Oggetto</th>
                <th>Acquirente</th>
                <th>Cliente</th>
                <th>Data</th>
                <th>Stato</th>
                <th className="bop-text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {preventivi.map(p => {
                const cliente = p.clienteRagioneSociale
                  ? p.clienteRagioneSociale
                  : [p.clienteNome, p.clienteCognome].filter(Boolean).join(' ') || '—';
                const totale = p.totaleCliente
                  ? formatCurrency(parseFloat(p.totaleCliente))
                  : '—';
                return (
                  <tr key={p.id}
                      className="bop-tab-preventivi__row"
                      onClick={() => apriPreventivo(p.id)}
                      style={{ cursor: 'pointer' }}>
                    <td>{p.numero}{p.versione > 1 ? ` (v${p.versione})` : ''}</td>
                    <td>{p.tipo === 'SEMPLICE' ? 'Semplice' : 'Configurato'}</td>
                    <td>{p.oggetto}</td>
                    <td>
                      {p.acquirente ? (
                        <span>
                          <strong>{p.acquirente.unitaCodice}</strong>
                          <br />
                          <span className="bop-text-muted">
                            {[p.acquirente.nome, p.acquirente.cognome].filter(Boolean).join(' ') || '—'}
                          </span>
                        </span>
                      ) : (
                        <span className="bop-text-muted">—</span>
                      )}
                    </td>
                    <td>{cliente}</td>
                    <td>{new Date(p.dataEmissione).toLocaleDateString('it-IT')}</td>
                    <td>{STATO_LABEL[p.stato] || p.stato}</td>
                    <td className="bop-text-right">{totale}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
